import * as React from 'react';
import { Formik } from 'formik';
import { ModalWindow } from '@cian/ui-kit';
import { useIsDesktop } from '@cian/valuation-utils-component';

import { DialogFooter, DialogHeader, LeaveContactsForm, ResultMessage } from '../../components/LeaveContacts';
import { validateLeaveContactsFormValues, useLeaveContactsDialogContainerProps } from './utils';
import failedImg from './assets/failed.png';
import succeedImg from './assets/succeed.png';
import { ILeaveContactsDialogContainerProps, ILeaveContactsFormValue, EFormStatus } from './types';
import {
  DEFAULT_DIALOG_TITLE,
  DEFAULT_DIALOG_SUBTITLE,
  DEFAULT_DIALOG_SUCCESS_MESSAGE,
  DEFAULT_DIALOG_FAIL_MESSAGE,
  FORM_INITIAL_VALUE,
} from './constants';

/**
 * LeaveContactsDialogContainer - форма для получения контактов пользователя в виде модального окна.
 */
export const LeaveContactsDialogContainer: React.FC<ILeaveContactsDialogContainerProps> = ({
  isOpen,
  title = DEFAULT_DIALOG_TITLE,
  subtitle = DEFAULT_DIALOG_SUBTITLE,
  successMessage = DEFAULT_DIALOG_SUCCESS_MESSAGE,
  failMessage = DEFAULT_DIALOG_FAIL_MESSAGE,
  calledFrom,
  onSendForm,
  onOpen,
  onClose,
}) => {
  const isDesktop = useIsDesktop();
  const { status, tryAgainHandler, closeHandler, sendHandler } = useLeaveContactsDialogContainerProps({
    onClose,
    onSendForm,
    calledFrom,
  });

  React.useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isPending = status === EFormStatus.Pending;

  return (
    <Formik<ILeaveContactsFormValue>
      initialValues={FORM_INITIAL_VALUE}
      onSubmit={sendHandler}
      validate={validateLeaveContactsFormValues}
      isInitialValid={false}
      validateOnMount
    >
      {({ handleSubmit, isSubmitting, resetForm, isValid }) => {
        const onTryAgain = tryAgainHandler(resetForm);
        const onClose = closeHandler(resetForm);

        return (
          <ModalWindow
            size="M"
            maxWidth={588}
            height={isDesktop ? 'auto' : '100%'}
            width="100%"
            open={isOpen}
            onClose={onClose}
            header={isPending && <DialogHeader title={title} subtitle={subtitle} />}
            footer={
              isPending && (
                <DialogFooter
                  isDesktop={isDesktop}
                  onClose={onClose}
                  onSend={handleSubmit}
                  isSubmitDisabled={isSubmitting || !isValid}
                />
              )
            }
          >
            {isPending && <LeaveContactsForm isDesktop={isDesktop} isSubmitting={isSubmitting} />}
            {status === EFormStatus.Failed && (
              <ResultMessage
                buttonText="Попробовать ещё раз"
                resultText={failMessage}
                isDesktop={isDesktop}
                onClickButton={onTryAgain}
                image={failedImg}
              />
            )}
            {status === EFormStatus.Succeed && (
              <ResultMessage
                buttonText="Ок, понятно"
                resultText={successMessage}
                isDesktop={isDesktop}
                onClickButton={onClose}
                image={succeedImg}
              />
            )}
          </ModalWindow>
        );
      }}
    </Formik>
  );
};
